import { useEffect, useState } from "react";
import * as yup from "yup";
import Tab from "components/Tabs/Tabs";
import Card from "components/ui/Card";
import Heading from "components/ui/Heading";
import resources from "resources.json";
import BasicDetails from "./BasicDetails";
import OtherActions from "./OtherActions";
import { useSearchParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { ActiveTab } from "./types";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetAccountGroups } from "./useGetAccountGroups";
import Spinner from "components/ui/Spinner";

const schema = yup.object().shape({
  newAccountGroupId: yup
    .string()
    .required(resources.errorMessage.NEW_ACCOUNT_GROUP_REQUIRED_ERROR),
});

export type FormValues = yup.InferType<typeof schema>;

export default function ChangeAccountGroup() {
  const [searchParams] = useSearchParams();

  const accountNumber = searchParams?.get("accountNumber") ?? "";
  const accountName = searchParams?.get("accountName") ?? "";
  const defaultTab = (searchParams?.get("defaultTab") ?? "BASIC") as ActiveTab;

  const [isOtherDetailsDisabled, setIsOtherDetailsDisabled] = useState(true);

  const [activeTab, setActiveTab] = useState<ActiveTab>(defaultTab);

  const { isLoading: isAccountGroupsLoading, data: accountGroupsData } =
    useGetAccountGroups({
      accountNumber,
    });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      newAccountGroupId: "",
    },
    resolver: yupResolver(schema),
  });

  const { reset } = formMethods;

  useEffect(() => {
    if (accountGroupsData?.isAccountGroupChangeAlreadyRequested) {
      setIsOtherDetailsDisabled(false);
      reset({
        newAccountGroupId: accountGroupsData?.newAccountGroupId,
      });
    }
  }, [accountGroupsData, reset])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as ActiveTab);
  };

  return (
    <FormProvider {...formMethods}>
      <Heading>
        <Heading.Title hasBackButton>
          {resources.title.CHANGE_ACCOUNT_GROUP_TITLE.replace(
            "{ACCOUNT_GROUP}",
            accountNumber,
          ).replace("{ACCOUNT_NAME}", accountName)}
        </Heading.Title>
      </Heading>

      <Card>
        {isAccountGroupsLoading ? (
          <Spinner />
        ) : (
          <>
            <Tab value={activeTab} onChange={handleTabChange}>
              <Tab.Heads>
                <Tab.Item label="Basic Detail" tabKey="BASIC" />
                <Tab.Item
                  label="Other Action Required"
                  tabKey="OTHER_ACTION"
                  disabled={isOtherDetailsDisabled}
                />
              </Tab.Heads>
            </Tab>


            {activeTab === "BASIC" && (
              <BasicDetails
                accountNumber={accountNumber}
                handleTabChange={handleTabChange}
              />
            )}

            {activeTab === "OTHER_ACTION" && <OtherActions />}
          </>
        )}
      </Card>
    </FormProvider>
  );
}
